// @ts-strict-ignore
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { uniqueId } from 'lodash-es';
import { UserFacadeService } from '../../../../store/user/user-facade.service';
import { InputBaseComponent } from '../input-base/input-base.component';

export enum CheckboxState {
  Added = 'added',
  Connected = 'connected',
  NotConnected = 'notConnected',
  Removed = 'removed',
  SomeConnected = 'someConnected',
  ChangedAndSomeConnected = 'changedAndSomeConnected',
}

const AriaChecked = {
  [CheckboxState.Added]: 'true',
  [CheckboxState.Connected]: 'true',
  [CheckboxState.NotConnected]: 'false',
  [CheckboxState.Removed]: 'false',
  [CheckboxState.SomeConnected]: 'mixed',
  [CheckboxState.ChangedAndSomeConnected]: 'mixed',
};

const Class = {
  [CheckboxState.Added]: 'added',
  [CheckboxState.Connected]: 'connected',
  [CheckboxState.NotConnected]: 'not-connected',
  [CheckboxState.Removed]: 'removed',
  [CheckboxState.SomeConnected]: 'some-connected',
  [CheckboxState.ChangedAndSomeConnected]: 'changed-and-some-connected',
  NumberZero: 'number-zero',
  NumberAboveZero: 'number-above-zero',
};

export interface CheckboxData {
  state: CheckboxState;
  info?: string | number;
}

interface Classes {
  'multistate-checkbox': boolean;
  added?: boolean;
  connected?: boolean;
  'not-connected'?: boolean;
  removed?: boolean;
  'changed-and-some-connected'?: boolean;
  'some-connected'?: boolean;
  'number-zero'?: boolean;
  'number-above-zero'?: boolean;
  info?: boolean;
  disabled?: boolean;
}

@Component({
  selector: 'ui-input-checkbox-multistate',
  templateUrl: './input-checkbox-multistate.component.html',
  styleUrls: ['./input-checkbox-multistate.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InputCheckboxMultistateComponent extends InputBaseComponent<CheckboxData> implements OnInit {
  userFacadeService = inject(UserFacadeService);
  @Input() data: any;
  @Input() info: string | number;
  @Input() name: string = uniqueId('checkbox-multistate');
  @Input() showInfo: boolean;
  @Input() set state(value: CheckboxState) {
    this._originalState = value;
    this._state = value;
  }
  get state(): CheckboxState {
    return this._state;
  }
  @Output() stateChange: EventEmitter<{ state: CheckboxState; data: any }> = new EventEmitter();
  @ViewChild('box') boxRef: ElementRef<HTMLDivElement>;

  classes: Classes = { 'multistate-checkbox': true };
  private _originalState: CheckboxState;
  private _state: CheckboxState;

  ngOnInit(): void {
    this._updateUI();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.info || changes.showInfo || changes.state) {
      this._updateUI();
    }
  }

  onCheckboxChange() {
    if (this.showInfo || (this.userFacadeService.isReadOnly() && !this.ignoreReadOnly)) {
      return;
    }
    if (!this._originalState) {
      this._originalState = !!this.formControlName ? this.value?.state : this._state;
    }
    switch (!!this.formControlName ? this.value?.state : this._state) {
      case CheckboxState.NotConnected:
        this._setAndEmit(CheckboxState.Added);
        break;
      case CheckboxState.SomeConnected:
        this._setAndEmit(CheckboxState.Added);
        break;
      case CheckboxState.Connected:
        this._setAndEmit(CheckboxState.Removed);
        break;
      case CheckboxState.Added:
        this._setAndEmit(CheckboxState.Removed);
        break;
      case CheckboxState.Removed:
        this._setAndEmit(CheckboxState.Added);
        break;
      case CheckboxState.ChangedAndSomeConnected:
        this._setAndEmit(CheckboxState.Added);
        break;
    }
  }

  private _updateUI(): void {
    const state = !!this.formControlName ? this.value?.state : this._state;
    const info = !!this.formControlName ? this.value?.info : this.info;

    this.classes = {
      'multistate-checkbox': true,
      [Class[state]]: !this.showInfo,
      [Class.NumberZero]: this.showInfo && info === 0,
      [Class.NumberAboveZero]: this.showInfo && typeof info === 'number' && info > 0,
      disabled: this.userFacadeService.isReadOnly() && !this.ignoreReadOnly,
      info: this.showInfo,
    };

    if (this.boxRef) {
      const boxEl = this.boxRef.nativeElement;
      this.showInfo ? boxEl.removeAttribute('aria-checked') : boxEl.setAttribute('aria-checked', AriaChecked[state]);
    }
  }

  private _setAndEmit(newState: CheckboxState): void {
    const validatedNewState = this._validateNewState(newState);

    if (!!this.formControlName) {
      this.writeValue({ ...this.value, state: newState });
      this.onTouched();
    } else {
      this._state = validatedNewState;
      this.stateChange.emit({ state: newState, data: this.data });
    }
    this._updateUI();
  }

  private _validateNewState(newState: CheckboxState): CheckboxState {
    const removedButWasOriginallyNotConnected =
      !!this._originalState && this._originalState === CheckboxState.NotConnected && newState === CheckboxState.Removed;
    const addedButWasOriginallyConnected =
      !!this._originalState && this._originalState === CheckboxState.Connected && newState === CheckboxState.Added;

    return removedButWasOriginallyNotConnected
      ? CheckboxState.NotConnected
      : addedButWasOriginallyConnected
        ? CheckboxState.Connected
        : newState;
  }
}
